import { authHeaders, type AuthArgs } from './authArgs';

export interface SupportCase {
  caseNumber: string;
  title: string | null;
  issueContext: string | null;
  state: string | null;
  stateAnnotation: string | null;
}

export type CaseHistoryFilter = 'active' | 'inactive';
export const CASE_HISTORY_LIMIT = 25;

export class CaseHistoryError extends Error {
  readonly status: number;

  constructor(status: number) {
    super('Unable to load case history.');
    this.name = 'CaseHistoryError';
    this.status = status;
  }
}

function isNullableText(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isSupportCase(value: unknown): value is SupportCase {
  return typeof value === 'object' && value !== null
    && 'caseNumber' in value && typeof value.caseNumber === 'string' && value.caseNumber.trim().length > 0
    && 'title' in value && isNullableText(value.title)
    && 'issueContext' in value && isNullableText(value.issueContext)
    && 'state' in value && isNullableText(value.state)
    && 'stateAnnotation' in value && isNullableText(value.stateAnnotation);
}

export async function getCaseHistory(
  auth: AuthArgs,
  filter: CaseHistoryFilter,
  signal: AbortSignal,
): Promise<SupportCase[]> {
  const response = await fetch('/api/v1/customer/cases/query', {
    method: 'POST',
    headers: authHeaders(auth),
    credentials: 'same-origin',
    cache: 'no-store',
    signal,
    body: JSON.stringify({
      filteredFields: [filter === 'active' ? 'State Active' : 'State ne Active'],
      top: CASE_HISTORY_LIMIT,
      orderBy: 'CreatedOn desc',
    }),
  });
  if (!response.ok) throw new CaseHistoryError(response.status);

  const body: unknown = await response.json();
  if (typeof body !== 'object' || body === null || !('cases' in body)
    || !Array.isArray(body.cases) || body.cases.length > CASE_HISTORY_LIMIT
    || !body.cases.every(isSupportCase)) {
    throw new CaseHistoryError(502);
  }
  return body.cases;
}
